import React, { useState, useEffect } from "react";
import Select from 'react-select';
import Modal from "../../../components/modal";
import { Minus, Plus } from "lucide-react";
import { Country, State } from 'country-state-city';
import axios from "axios";
import { API_URL } from "../../../utils/constant";
import CreatableSelect from "react-select/creatable";
import { useDispatch, useSelector } from "react-redux";
import { fetchDispenseMaterials } from "../../../slices/company/dispenseMaterialSlice";


const PurchasesProductForm = ({ show, onClose, heading, initialData = {}, onSubmit, categories, suppliers, rawProducts, dispenseMaterials: propDispenseMaterials, viewOnly }) => {
    const dispatch = useDispatch();
    const { materials: reduxDispenseMaterials = [] } = useSelector(state => state.dispenseMaterial || { materials: [] });
    const dispenseMaterials = (propDispenseMaterials && propDispenseMaterials.length > 0) ? propDispenseMaterials : reduxDispenseMaterials;

    // State to manage form inputs
    const [formData, setFormData] = useState({
        vendor_id: '',
        country: 'India',
        state: 'West Bengal',
        bill_date: '',
        due_date: '',
        bill_number: '',
        order_number: '',
        total_qty: '',
        total_taxable_value: '',
        product_discount: '',
        additional_discount: '',
        return_amount: '',
        advance_vendor_payment: '',
        cgst: '',
        sgst: '',
        gst: '',
        total_amount: '',
        raw_products: [{ item_type: 'Product', id: '', item_name: '', quantity: '', per_unit_actual_price: '', per_unit_purchase_price: '',discount: '', price: '', cgst:'6', sgst:'6', gst: '12', discount_type: 'flat',  total_amount: '', hsn_sac:'', description: '' }]
    });
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('India'); // India
    const [selectedState, setSelectedState] = useState('West Bengal'); // West Bengal
    const [companyData, setCompanyData] = useState([]);
    const [vendorRawProducts, setVendorRawProducts] = useState(null);


    const fetchCompany = async () => {
        try {
            const company = JSON.parse(localStorage.getItem('company'));
            const companyId = company.company.id;
            const res = await axios.get(`${API_URL}/companies/${companyId}`);
            const data = res.data;
            setCompanyData(data);
        } catch (err) {
            console.error("Failed to fetch company:", err);
        }
    };

    const fetchVendorRawProducts = async (vendorId) => {
        if (!vendorId) {
            setVendorRawProducts(null);
            return;
        }
        try {
            const company = JSON.parse(localStorage.getItem('company'));
            const companyId = company.company.id;
            const res = await axios.get(`${API_URL}/raw-products/get-vendor/raw-products/data/${vendorId}?company_id=${companyId}`);
            setVendorRawProducts(res.data);
        } catch (err) {
            console.error("Failed to fetch vendor raw products:", err);
            setVendorRawProducts(null);
        }
    };

    
    useEffect(() => {
        fetchCompany();
        setCountries(Country.getAllCountries());
        const company = JSON.parse(localStorage.getItem('company'));
        if (company?.company?.id) {
            dispatch(fetchDispenseMaterials(company.company.id));
        }
    }, [dispatch]);

    const itemTypeOptions = [
        { value: 'Product', label: 'Product' },
        { value: 'DispenseMaterial', label: 'Dispensing Material' },
        { value: 'Packaging', label: 'Packaging' },
        { value: 'Shopping Bag', label: 'Shopping Bag' },
        { value: 'Return', label: 'Return' },
        { value: 'Other', label: 'Other' }
    ];

    const getItemTypeValue = (type) => {
        if (!type || type === 'Product') return { value: 'Product', label: 'Product' };
        if (type === 'DispenseMaterial' || type === 'Dispensing Material') return { value: 'DispenseMaterial', label: 'Dispensing Material' };
        return itemTypeOptions.find(o => o.value === type) || { value: type, label: type };
    };
    
      // Load state list when country changes
    useEffect(() => {
        if (!selectedCountry || !countries.length) return;

        const countryData = countries.find(c => c.name === selectedCountry);
        if (countryData?.isoCode) {
            const countryStates = State.getStatesOfCountry(countryData.isoCode);
            setStates(countryStates);
            setSelectedState(""); 
        }
    }, [selectedCountry, countries]);

    useEffect(() => {
        setSelectedState("West Bengal");
    }, [selectedCountry]);

    useEffect(() => {
        fetchVendorRawProducts(formData.vendor_id);
    }, [formData.vendor_id]);

    // Auto-calculate summary totals whenever raw_products changes
    useEffect(() => {
        const raw_products = formData.raw_products || [];

        // ================= NORMAL ITEMS (Skip Return) =================

        const total_qty = raw_products.reduce((sum, p) => {
            if (p.item_type === 'Return') return sum;
            return sum + (parseFloat(p.quantity) || 0);
        }, 0);

        const total_taxable_value = raw_products.reduce((sum, p) => {
            if (p.item_type === 'Return') return sum;
            return sum + (parseFloat(p.price) || 0);
        }, 0);

        const product_discount = raw_products.reduce((sum, p) => {
            if (p.item_type === 'Return') return sum;

            const qty = parseFloat(p.quantity) || 1;
            const discount = parseFloat(p.discount) || 0;

            if (p.discount_type === 'percentage') {
                const actualPrice = parseFloat(p.per_unit_actual_price) || 0;
                return sum + ((actualPrice * qty * discount) / 100);
            } else {
                return sum + (discount * qty);
            }
        }, 0);

        let total_cgst = 0;
        let total_sgst = 0;
        let total_gst = 0;

        // ================= RETURN ITEMS =================

        let return_amount = 0;

        raw_products.forEach((product) => {
            const price = parseFloat(product.price) || 0;

            // -------- RETURN ITEMS CALC --------
            if (product.item_type === 'Return') {
                let tax = 0;

                if (companyData?.state === formData.state) {
                    tax += (price * (parseFloat(product.cgst) || 0)) / 100;
                    tax += (price * (parseFloat(product.sgst) || 0)) / 100;
                } else {
                    tax += (price * (parseFloat(product.gst) || 0)) / 100;
                }

                return_amount += price + tax;
                return;
            }

            // -------- NORMAL ITEMS TAX --------
            if (companyData?.state === formData.state) {
                total_cgst += (price * (parseFloat(product.cgst) || 0)) / 100;
                total_sgst += (price * (parseFloat(product.sgst) || 0)) / 100;
            } else {
                total_gst += (price * (parseFloat(product.gst) || 0)) / 100;
            }
        });

        const total_tax = total_cgst + total_sgst + total_gst;
        const additional_discount = parseFloat(formData.additional_discount) || 0;

        const total_amount =
            total_taxable_value + total_tax - additional_discount;

        setFormData(prev => ({
            ...prev,
            total_qty: +total_qty.toFixed(2),
            total_taxable_value: +total_taxable_value.toFixed(2),
            product_discount: +product_discount.toFixed(2),
            cgst: +total_cgst.toFixed(2),
            sgst: +total_sgst.toFixed(2),
            gst: +total_gst.toFixed(2),
            total_amount: +total_amount.toFixed(2),

            // ✅ NEW FIELD
            return_amount: +return_amount.toFixed(2),
        }));
    }, [
        formData.raw_products,
        formData.additional_discount,
        companyData?.state,
        formData.state
    ]);



    const isviewonly = heading == 'View Purchases';

    const closeModel = () => {
        setFormData({
            vendor_id: '',
            country: 'India',
            state: 'West Bengal',
            bill_date: '',
            due_date: '',
            bill_number: '',
            order_number: '',
            total_qty: '',
            total_taxable_value: '',
            product_discount: '',
            additional_discount: '',
            cgst: '',
            sgst: '',
            gst: '',
            advance_vendor_payment: '',
            total_amount: '',
            raw_products: [{ item_type: 'Product', id: '', item_name: '', quantity: '',per_unit_actual_price: '', per_unit_purchase_price: '',discount: '', price: '',cgst:'6', sgst:'6', gst: '12', discount_type: 'flat', total_amount: '',hsn_sac:'', description: '' }]
        });
        onClose();
    }
    // Load initial data into form if editing an existing tax
    useEffect(() => {
        if (initialData) {
            setFormData({
                vendor_id: initialData.vendor_id || '',
                country: initialData.country || 'India',
                state: initialData.state || 'West Bengal',
                bill_date: initialData.bill_date ? initialData.bill_date.split('T')[0] : '',
                due_date: initialData.due_date ? initialData.due_date.split('T')[0] : '',
                bill_number: initialData.bill_number || '',
                order_number: initialData.order_number || '',
                total_qty: initialData.total_qty || '',
                total_taxable_value: initialData.total_taxable_value || '',
                product_discount: initialData.product_discount || '',
                advance_vendor_payment: initialData.advance_vendor_payment || '',
                additional_discount: initialData.additional_discount || '',
                cgst: initialData.cgst || '',
                sgst: initialData.sgst || '',
                gst: initialData.gst || '',
                total_amount: initialData.total_amount || '',
            });

            if (initialData.Products) {
                setFormData(prevFormData => ({
                    ...prevFormData,
                    raw_products: initialData.Products.map(product => ({
                        item_type: product.item_type || 'Product',
                        item_name: product.item_name || '',
                        per_unit_actual_price: product.per_unit_actual_price || '', 
                        per_unit_purchase_price: product.per_unit_purchase_price || '',
                        hsn_sac: product.hsn_sac || '', 
                        cgst: product.cgst || '6', 
                        sgst: product.sgst || '6', 
                        id: product.product_id || '',
                        quantity: product.quantity || 1,
                        price: product.price || '',
                        discount_type: product.discount_type || 'flat',
                        discount: product.discount || 0,
                        total_amount: product.total_amount || '',
                        gst: product.gst || '12',
                        description: product.description || ''
                    }))
                }));
            }
        }
    }, [initialData]);


    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prevFormData => {
            let updatedFormData = { ...prevFormData, [name]: value };

            // Reset raw_products if vendor_id changes
            if (name === 'vendor_id') {
                updatedFormData.raw_products = [{ item_type: 'Product', id: '', item_name: '', quantity: '',per_unit_actual_price: '', per_unit_purchase_price: '',discount: '', price: '',cgst:'6', sgst:'6', gst: '12', discount_type: 'flat', total_amount: '',hsn_sac:'', description: '' }];
            }

            return updatedFormData;
        });
    };

    const handleDynamicChange = (e, index, field) => {
        const { value } = e.target;

        const updatedProducts = formData.raw_products.map((product, idx) => {
            if (idx === index) {
                let updatedProduct = { ...product, [field]: value };

                if (field === "item_type") {
                    updatedProduct.id = '';
                    updatedProduct.item_name = '';
                    if (value === 'DispenseMaterial' || value === 'Packaging' || value === 'Dispensing Material') {
                        updatedProduct.cgst = '6';
                        updatedProduct.sgst = '6';
                        updatedProduct.gst = '12';
                        updatedProduct.hsn_sac = '39235010';
                    }
                }

                // Ensure ID is valid before fetching purchase cost
                if (field === "id") {
                    if (updatedProduct.item_type === 'DispenseMaterial' || updatedProduct.item_type === 'Packaging' || updatedProduct.item_type === 'Dispensing Material' || updatedProduct.item_type === 'Shopping Bag') {
                        const selectedMat = dispenseMaterials.find(m => m.id == value);
                        if (selectedMat) {
                            updatedProduct.id = selectedMat.id;
                            updatedProduct.item_name = selectedMat.name;
                            const taxRate = parseFloat(selectedMat.gst) || 12;
                            const hsnCode = selectedMat.hsn_code || (selectedMat.material_type === 'bottle' ? '70109000' : '39235010');
                            const purchaseCost = parseFloat(selectedMat.cost_price || selectedMat.price) || 0;
                            const qty = parseFloat(updatedProduct.quantity) || 1;
                            updatedProduct.price = parseFloat((purchaseCost * qty).toFixed(2));
                            updatedProduct.per_unit_purchase_price = purchaseCost;
                            updatedProduct.per_unit_actual_price = parseFloat(selectedMat.price || purchaseCost) || purchaseCost;
                            updatedProduct.cgst = +(taxRate / 2).toFixed(2);
                            updatedProduct.sgst = +(taxRate / 2).toFixed(2);
                            updatedProduct.gst = taxRate;
                            updatedProduct.hsn_sac = hsnCode;

                            let totalAmount = updatedProduct.price;
                            let totalTax = 0;
                            if (companyData?.state === (selectedState || formData.state)) {
                                totalTax = (taxRate / 2) + (taxRate / 2);
                            } else {
                                totalTax = taxRate;
                            }
                            totalAmount += (totalAmount * totalTax / 100);
                            updatedProduct.total_amount = totalAmount > 0 ? parseFloat(totalAmount.toFixed(2)) : 0;
                        }
                    } else {
                        const selectedProduct = getAvailableRawProducts().find(p => p.id == value);
                        if (selectedProduct) {
                            updatedProduct.id = selectedProduct.id;
                            updatedProduct.item_name = selectedProduct.name;
                            const vendorData = selectedProduct.RawProductVendors?.find(vendor => vendor.vendor_id == formData.vendor_id);
                            const taxRate = parseFloat(selectedProduct?.TaxRate?.rate) || 0;
                            const hsnCode = selectedProduct?.TaxRate?.hsn_code || "";
                            let cgst = taxRate / 2;
                            let sgst = taxRate / 2;
                            let gst = taxRate;
                            const purchaseCost = vendorData ? vendorData.purchase_cost : 0;
                            updatedProduct.price = purchaseCost;
                            updatedProduct.quantity = 1;
                            updatedProduct.total_amount = purchaseCost;
                            updatedProduct.cgst = cgst;
                            updatedProduct.sgst = sgst;
                            updatedProduct.gst  = gst;
                            updatedProduct.hsn_sac = hsnCode;
                        }
                    }
                }

                // Auto-calculate total price if quantity is updated
                if (field === "quantity") {
                    const quantity = parseFloat(value) || 1;
                    
                    // Use per_unit_purchase_price if available, otherwise use vendor's purchase cost or material cost
                    let unitPrice = parseFloat(updatedProduct.per_unit_purchase_price) || 0;
                    
                    if (unitPrice === 0) {
                        if (updatedProduct.item_type === 'DispenseMaterial' || updatedProduct.item_type === 'Packaging' || updatedProduct.item_type === 'Dispensing Material') {
                            const selectedMat = dispenseMaterials.find(m => m.id == updatedProduct.id);
                            unitPrice = selectedMat ? (parseFloat(selectedMat.cost_price || selectedMat.price) || 0) : 0;
                        } else {
                            const selectedProduct = getAvailableRawProducts().find(p => p.id == updatedProduct.id);
                            if (selectedProduct) {
                                const vendorData = selectedProduct.RawProductVendors?.find(vendor => vendor.vendor_id == formData.vendor_id);
                                unitPrice = vendorData ? vendorData.purchase_cost : 0;
                            }
                        }
                    }
                    
                    // Calculate price without tax first
                    updatedProduct.price = unitPrice * quantity;
                    
                    // Calculate total with GST
                    let totalAmount = updatedProduct.price;
                    let totalTax;
                    if(companyData?.state == selectedState){
                        const cgst = parseFloat(updatedProduct.cgst) || 0;
                        const sgst = parseFloat(updatedProduct.sgst) || 0;
                        totalTax = cgst + sgst;
                    }else{
                        totalTax = parseFloat(updatedProduct.gst) || 0;
                    }
                   
                    totalAmount += (totalAmount * totalTax / 100);
                    updatedProduct.total_amount = totalAmount > 0 ? parseFloat(totalAmount.toFixed(2)) : 0;
                }

                // Auto-calculate discount from actual_price and purchase_price
                if (field === "per_unit_actual_price" || field === "per_unit_purchase_price") {
                    const actualPrice = parseFloat(updatedProduct.per_unit_actual_price) || 0;
                    const purchasePrice = parseFloat(updatedProduct.per_unit_purchase_price) || 0;
                    
                    // Calculate discount if both actual and purchase prices are set
                    if (actualPrice > 0 && purchasePrice >= 0) {
                        const discountAmount = actualPrice - purchasePrice;
                        updatedProduct.discount = discountAmount > 0 ? parseFloat(discountAmount.toFixed(2)) : 0;
                        updatedProduct.discount_type = 'flat'; // Use flat discount when calculating from prices
                    }
                    
                    // Auto-calculate price and total_amount
                    const quantity = parseFloat(updatedProduct.quantity) || 1;
                    updatedProduct.price = purchasePrice * quantity;
                    
                    // Calculate total with GST
                    let totalAmount = updatedProduct.price;
                    let totalTax;
                    if(companyData?.state == selectedState){
                        const cgst = parseFloat(updatedProduct.cgst) || 0;
                        const sgst = parseFloat(updatedProduct.sgst) || 0;
                        totalTax = cgst + sgst;
                    }else{
                        totalTax = parseFloat(updatedProduct.gst) || 0;
                    }
                   
                    totalAmount += (totalAmount * totalTax / 100);
                    updatedProduct.total_amount = totalAmount > 0 ? parseFloat(totalAmount.toFixed(2)) : 0;
                }

                // Auto-calculate purchase_price from actual_price and discount
                if (field === "discount" || field === "discount_type") {
                    const actualPrice = parseFloat(updatedProduct.per_unit_actual_price) || 0;
                    const discount = parseFloat(updatedProduct.discount) || 0;
                    const discountType = updatedProduct.discount_type || 'flat';
                    
                    let purchasePrice = actualPrice;
                    if (discountType === 'percentage') {
                        purchasePrice = actualPrice - (actualPrice * discount / 100);
                    } else if (discountType === 'flat') {
                        purchasePrice = actualPrice - discount;
                    }
                    
                    updatedProduct.per_unit_purchase_price = purchasePrice > 0 ? parseFloat(purchasePrice.toFixed(2)) : 0;
                    
                    // Auto-calculate price and total_amount
                    const quantity = parseFloat(updatedProduct.quantity) || 1;
                    updatedProduct.price = purchasePrice * quantity;
                    
                    // Calculate total with GST
                    let totalAmount = updatedProduct.price;
                    let totalTax;
                    if(companyData?.state == selectedState){
                        const cgst = parseFloat(updatedProduct.cgst) || 0;
                        const sgst = parseFloat(updatedProduct.sgst) || 0;
                        totalTax = cgst + sgst;
                    }else{
                        totalTax = parseFloat(updatedProduct.gst) || 0;
                    }
                   
                    totalAmount += (totalAmount * totalTax / 100);
                    updatedProduct.total_amount = totalAmount > 0 ? parseFloat(totalAmount.toFixed(2)) : 0;
                }

                // Auto-sync SGST with CGST when either changes
                if (field === "cgst") {
                    updatedProduct.sgst = value;
                }
                if (field === "sgst") {
                    updatedProduct.cgst = value;
                }

                // Auto-calculate total amount if price, cgst, or sgst changes
                if (["price", "cgst", "sgst", "gst"].includes(field)) {
                    const price = parseFloat(updatedProduct.price) || 0;
                    let tax = 0;
                    if(companyData?.state == selectedState){
                        const cgst = parseFloat(updatedProduct.cgst) || 0;
                        const sgst = parseFloat(updatedProduct.sgst) || 0;
                        tax = cgst + sgst;
                    }else{
                        tax = parseFloat(updatedProduct.gst) || 0;
                    }
                    
                    const totalTax = tax;
                    let totalAmount = price + (price * totalTax / 100);
                    updatedProduct.total_amount = totalAmount > 0 ? parseFloat(totalAmount.toFixed(2)) : 0;
                }

                // Auto-calculate total amount if total_amount or gst changes
                if (["total_amount", "gst"].includes(field)) {
                    if (field === "total_amount" || (field === "gst" && updatedProduct.total_amount)) {
                        const cgst = parseFloat(updatedProduct.cgst) || 0;
                        const sgst = parseFloat(updatedProduct.sgst) || 0;
                        const gst = parseFloat(updatedProduct.gst) || 0;
                        let totalTax = 0;
                        if(companyData?.state == selectedState){
                            totalTax = cgst + sgst;
                        }else{
                            totalTax = gst;
                        }
                        const totalAmount = parseFloat(updatedProduct.total_amount) || 0;
                        let price = totalTax ? totalAmount / (1 + totalTax / 100) : totalAmount;
                        updatedProduct.price = price > 0 ? parseFloat(price.toFixed(2)) : 0;
                    }
                }

                return updatedProduct;
            }
            return product;
        });

        // Simply update raw_products, useEffect will handle totals calculation
        setFormData({ ...formData, raw_products: updatedProducts });
    };


    // Add a new row to raw products
    const handleAddRow = () => {
        setFormData({
            ...formData,
            raw_products: [...formData.raw_products, { item_type: 'Product', id: '', item_name: '', quantity: '', per_unit_actual_price: '', per_unit_purchase_price: '',discount: '', price: '', cgst:'6', sgst:'6', gst: '12', discount_type: 'flat', total_amount: '',hsn_sac:'', description: '' }]
        });
    };

    // Handle form submission
    const handleSubmit = (e) => {
        console.log('data', formData);
        e.preventDefault();
        onSubmit(formData);
    };


    const getAvailableRawProducts = () => {
        return vendorRawProducts || rawProducts;
    };

    const getUnit = (id, rawProduct) => {
        if (rawProduct && (rawProduct.item_type === 'DispenseMaterial' || rawProduct.item_type === 'Packaging' || rawProduct.item_type === 'Dispensing Material' || rawProduct.item_type === 'Shopping Bag')) {
            const mat = dispenseMaterials.find(m => m.id == id || (rawProduct.item_name && m.name?.toLowerCase() === rawProduct.item_name?.toLowerCase()));
            return mat?.unit || 'Pcs';
        }
        const prod = getAvailableRawProducts().find((e) => e.id == id);
        if (prod) {
            return prod.RawCategory?.Unit?.name;
        }
        return "";
    };

    const getPurchaseItemTotalDetails = () => {
        const raw_products = formData.raw_products || [];
        
        // Calculate total quantity
        const total_qty = raw_products.reduce((sum, product) => {
            return sum + (parseFloat(product.quantity) || 0);
        }, 0);
        
        // Calculate total taxable value (price without tax)
        const total_taxable_value = raw_products.reduce((sum, product) => {
            return sum + (parseFloat(product.price) || 0);
        }, 0);
        
        // Calculate product discount
        const product_discount = raw_products.reduce((sum, product) => {
            const quantity = parseFloat(product.quantity) || 1;
            const discount = parseFloat(product.discount) || 0;
            
            // Calculate discount amount based on discount type
            if (product.discount_type === 'percentage') {
                const actualPrice = parseFloat(product.per_unit_actual_price) || 0;
                return sum + ((actualPrice * quantity * discount) / 100);
            } else {
                return sum + (discount * quantity);
            }
        }, 0);
        
        // Calculate total tax
        let total_tax = 0;
        raw_products.forEach((product) => {
            const price = parseFloat(product.price) || 0;
            let tax_rate = 0;
            
            if (companyData?.state == formData.state) {
                const cgst = parseFloat(product.cgst) || 0;
                const sgst = parseFloat(product.sgst) || 0;
                tax_rate = cgst + sgst;
            } else {
                tax_rate = parseFloat(product.gst) || 0;
            }
            
            total_tax += (price * tax_rate / 100);
        });
        
        // Calculate total amount (taxable value + total tax + additional discount)
        const additional_discount = parseFloat(formData.additional_discount) || 0;
        const total_amount = total_taxable_value + total_tax - additional_discount;
        
        return {
            total_qty: parseFloat(total_qty.toFixed(2)),
            total_taxable_value: parseFloat(total_taxable_value.toFixed(2)),
            product_discount: parseFloat(product_discount.toFixed(2)),
            total_tax: parseFloat(total_tax.toFixed(2)),
            total_amount: parseFloat(total_amount.toFixed(2))
        };
    }

    return (
        <Modal
            show={show}
            onClose={closeModel}
            heading={heading}
            size="xl"
            style={{
                position: "fixed"
            }}
        >
            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-md-4">
                        <div className="form-group mb-4">
                            <label>Supplier</label>
                            <select
                                name="vendor_id"
                                className="form-control"
                                value={formData.vendor_id}
                                onChange={(e) => {
                                    const selectedVendorId = e.target.value;

                                    const selectedSupplier = suppliers.find(
                                        (s) => s.id === Number(selectedVendorId)
                                    );
                                    console.log('seeclt', selectedSupplier);

                                    setFormData(prev => ({
                                        ...prev,
                                        vendor_id: selectedVendorId,
                                        country: selectedSupplier?.billing_country || "India",
                                        state: selectedSupplier?.billing_state || "West Bengal",
                                    }));
                                    handleChange(e);
                                }}
                                readOnly={isviewonly}
                            >
                                <option value={''}>Select Supplier</option>
                                {suppliers.map((data) => <option key={data.id} value={data.id}>{data.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="form-group mb-4">
                            <label>Country</label>
                            <select
                                className='form-control'
                                value={formData.country}
                                name="country"
                                onChange={e => {
                                    handleChange(e)
                                    setSelectedCountry(e.target.value);
                                    setSelectedState('');
                                }}
                                >
                                {countries.map(c => (
                                    <option key={c.isoCode} value={c.name}>
                                    {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    
                    <div className="col-md-4">
                        <div className="form-group mb-4">
                            <label>State</label>
                            <select
                                className='form-control'
                                value={formData.state}
                                name="state"
                                onChange={e => {
                                    handleChange(e)
                                    setSelectedState(e.target.value);
                                }}
                                >
                                <option value="">-- Select State --</option>
                                {states.map(s => (
                                    <option key={s.isoCode} value={s.name}>
                                    {s.name}
                                    </option>
                                ))}
                                </select>
                        </div>
                    </div>

                    <div className="col-md-3">
                        <div className="form-group mb-4">
                            <label>Bill Date</label>
                            <input
                                type="date"
                                name="bill_date"
                                readOnly={isviewonly}
                                className="form-control"
                                value={formData.bill_date}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="col-md-3">
                        <div className="form-group mb-4">
                            <label>Receiving Date</label>
                            <input
                                type="date"
                                name="due_date"
                                readOnly={isviewonly}
                                className="form-control"
                                value={formData.due_date}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="form-group mb-4">
                            <label>Bill Number</label>
                            <input
                                type="text"
                                name="bill_number"
                                readOnly={isviewonly}
                                className="form-control"
                                value={formData.bill_number}
                                onChange={handleChange}
                                placeholder="Enter Bill Number"
                                required
                            />
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="form-group mb-4">
                            <label>Order Number</label>
                            <input
                                type="text"
                                name="order_number"
                                readOnly={isviewonly}
                                className="form-control"
                                value={formData.order_number}
                                onChange={handleChange}
                                placeholder="Enter Order Number"
                                required
                            />
                        </div>
                    </div>

                    {/* Render all supplier-cost pairs */}
                    <div className="col-md-12" style={{
                        height: '400px',
                        overflow: 'scroll',
                        background: '#1a18180f'

                    }}>
                        <label>Purchased Items</label>
                        {formData.raw_products.map((rawProduct, index) => {
                            const isDispensingItem = rawProduct.item_type === "DispenseMaterial" || rawProduct.item_type === "Dispensing Material" || rawProduct.item_type === "Packaging" || rawProduct.item_type === "Shopping Bag";
                            const isProductItem = !rawProduct.item_type || rawProduct.item_type === "Product";
                            const isReturnItem = rawProduct.item_type === "Return";
                            const isOtherItem = rawProduct.item_type === "Other";

                            return (
                                <div key={index} style={{
                                    borderTop: "1px solid #eee",

                                    borderBottom: "1px solid #000"
                                }}>
                                    <div className="row" style={{
                                        margin: "15px 0px",
                                    }}>
                                        <div className="col-md-3">
                                            <div className="form-group">
                                                <label>Item Type</label>
                                                <Select
                                                    classNamePrefix="react-select"
                                                    name="item_type"
                                                    isDisabled={isviewonly}
                                                    value={getItemTypeValue(rawProduct.item_type)}
                                                    options={itemTypeOptions}
                                                    placeholder="Select Type"
                                                    onChange={(e) => handleDynamicChange({target: {value: e?.value || 'Product'}}, index, 'item_type')}
                                                />
                                            </div>
                                        </div>


                                        {/* CONDITIONAL RENDER: PRODUCT */}
                                        {isProductItem && (
                                            <div className={`${isviewonly ? 'col-md-3' : 'col-md-3'}`}>
                                                <div className="form-group mb-4">
                                                    <label>Product</label>

                                                    {!isviewonly ? (
                                                    <Select
                                                        name="id"
                                                        classNamePrefix="react-select"
                                                        value={getAvailableRawProducts()
                                                            .filter(p => !formData.vendor_id || p.RawProductVendors?.some(v => v.vendor_id == formData.vendor_id))
                                                            .map(p => ({ value: p.id, label: p.name }))
                                                            .find(option => option.value == rawProduct.id) || (rawProduct.item_name ? { value: rawProduct.id, label: rawProduct.item_name } : null)}
                                                        onChange={(option) =>
                                                            handleDynamicChange(
                                                                { target: { value: option?.value || "" } },
                                                                index,
                                                                "id"
                                                            )
                                                        }
                                                        options={getAvailableRawProducts()
                                                            .filter(p => !formData.vendor_id || p.RawProductVendors?.some(v => v.vendor_id == formData.vendor_id))
                                                            .map(p => ({ value: p.id, label: p.name }))}
                                                        isClearable
                                                        isSearchable
                                                        placeholder="Select Raw Product"
                                                    />

                                                    ) : (
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        className="form-control"
                                                        value={
                                                            rawProduct.item_name ||
                                                            getAvailableRawProducts().find(p => p.id == rawProduct.id)?.name ||
                                                            "-"
                                                        }
                                                    />

                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* CONDITIONAL RENDER: DISPENSING MATERIAL */}
                                        {isDispensingItem && (
                                            <div className="col-md-3">
                                                <div className="form-group mb-4">
                                                    <label>Dispensing Item</label>
                                                    {!isviewonly ? (
                                                        <CreatableSelect
                                                            classNamePrefix="react-select"
                                                            placeholder="Select Dispensing Material"
                                                            isClearable
                                                            isSearchable
                                                            value={
                                                                (rawProduct.id && dispenseMaterials.find(m => m.id == rawProduct.id))
                                                                    ? { value: rawProduct.id, label: `${dispenseMaterials.find(m => m.id == rawProduct.id).name} (${dispenseMaterials.find(m => m.id == rawProduct.id).unit || 'Pcs'})` }
                                                                    : (rawProduct.item_name
                                                                        ? { value: rawProduct.id || rawProduct.item_name, label: rawProduct.item_name }
                                                                        : null)
                                                            }
                                                            options={dispenseMaterials.map(m => ({
                                                                value: m.id,
                                                                label: `${m.name} (${m.unit || 'Pcs'})`,
                                                                item_name: m.name,
                                                                material: m
                                                            }))}
                                                            onChange={(option) => {
                                                                if (!option) {
                                                                    handleDynamicChange({ target: { value: '' } }, index, 'id');
                                                                    handleDynamicChange({ target: { value: '' } }, index, 'item_name');
                                                                    return;
                                                                }
                                                                const selectedMat = dispenseMaterials.find(m => m.id == option.value) || option.material;
                                                                if (selectedMat) {
                                                                    handleDynamicChange({ target: { value: selectedMat.id } }, index, 'id');
                                                                } else {
                                                                    handleDynamicChange({ target: { value: '' } }, index, 'id');
                                                                    handleDynamicChange({ target: { value: option.label || option.value } }, index, 'item_name');
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            readOnly
                                                            className="form-control"
                                                            value={
                                                                rawProduct.item_name ||
                                                                dispenseMaterials.find(m => m.id == rawProduct.id)?.name ||
                                                                "-"
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {isReturnItem && (
                                            <div className="col-md-3">
                                                <div className="form-group">
                                                    <label>Return Item</label>
                                                    {!isviewonly ? (<CreatableSelect
                                                        classNamePrefix="react-select"
                                                        placeholder="Select or type item"
                                                        isClearable
                                                        isSearchable
                                                        value={
                                                            rawProduct.item_name
                                                                ? { label: rawProduct.item_name, value: rawProduct.item_name }
                                                                : null
                                                        }
                                                        options={getAvailableRawProducts().map(p => ({
                                                            label: p.name,
                                                            value: p.name,
                                                        }))}
                                                        onChange={(option) =>
                                                            handleDynamicChange(
                                                                { target: { value: option?.value || "" } },
                                                                index,
                                                                "item_name"
                                                            )
                                                        }
                                                    />):(<input
                                                        type="text"
                                                        name="item_name"
                                                        disabled
                                                        className="form-control"
                                                        placeholder="Enter item name"
                                                        value={rawProduct.item_name || ""}
                                                        onChange={(e) => handleDynamicChange(e, index, 'item_name')}
                                                    />)}

                                                </div>
                                            </div>
                                        )}
                                        {/* IF item_type = other → show text field */}
                                        {isOtherItem && (
                                            <div className="col-md-3">
                                                <div className="form-group">
                                                    <label>Other Item</label>
                                                    <input
                                                        type="text"
                                                        name="item_name"
                                                        disabled={isviewonly}
                                                        className="form-control"
                                                        placeholder="Enter item name"
                                                        value={rawProduct.item_name || ""}
                                                        onChange={(e) => handleDynamicChange(e, index, 'item_name')}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Quantity */}
                                        <div className="col-md-2">
                                            <div className="form-group mb-4">
                                                <label>Quantity</label>
                                                <div className="input-group mb-2">
                                                    <input
                                                        type="number"
                                                        readOnly={isviewonly}
                                                        name="quantity"
                                                        className="form-control"
                                                        value={rawProduct.quantity}
                                                        onChange={(e) => handleDynamicChange(e, index, 'quantity')}
                                                        placeholder="Enter Quantity"
                                                    />
                                                    <div className="input-group-prepend">
                                                        <div className="input-group-text h-100">{getUnit(rawProduct.id, rawProduct) || "Pcs"}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-2">
                                            <div className="form-group mb-4">
                                                <label>Per Unit Actual Price</label>
                                                <div className="input-group mb-2">
                                                    <input
                                                        type="number"
                                                        readOnly={isviewonly}
                                                        name="per_unit_actual_price"
                                                        className="form-control"
                                                        value={rawProduct.per_unit_actual_price}
                                                        onChange={(e) => handleDynamicChange(e, index, 'per_unit_actual_price')}
                                                        placeholder="Enter Quantity"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-group mb-4">
                                                <label>Per Unit Purchase Price</label>
                                                <div className="input-group mb-2">
                                                    <input
                                                        type="number"
                                                        readOnly={isviewonly}
                                                        name="per_unit_purchase_price"
                                                        className="form-control"
                                                        value={rawProduct.per_unit_purchase_price}
                                                        onChange={(e) => handleDynamicChange(e, index, 'per_unit_purchase_price')}
                                                        placeholder="Enter Quantity"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-2">
                                            <div className="form-group mb-4">
                                                <label>Discount</label>
                                                <div className="input-group mb-2">
                                                    {rawProduct.discount_type && (<div className="input-group-prepend">
                                                        <div className="input-group-text h-100">{rawProduct.discount_type == 'percentage' ? '%' : '₹'}</div>
                                                    </div>)}
                                                    <input
                                                        type="number"
                                                        name="discount"
                                                        readOnly={isviewonly}
                                                        className="form-control"
                                                        value={rawProduct.discount}
                                                        onChange={(e) => handleDynamicChange(e, index, 'discount')}
                                                        placeholder="Enter Discount"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Price */}
                                        <div className="col-md-3">
                                            <div className="form-group mb-4">
                                                <label>Price</label>
                                                <div className="input-group">
                                                    <div className="input-group-prepend">
                                                        <div className="input-group-text h-100">₹</div>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        name="price"
                                                        readOnly={isviewonly}
                                                        className="form-control"
                                                        value={rawProduct.price}
                                                        onChange={(e) => handleDynamicChange(e, index, 'price')}
                                                        placeholder="Enter Price"
                                                    />

                                                </div>
                                            </div>
                                        </div>

                                        {companyData?.state == formData.state ? (
                                            <>
                                                <div className="col-md-2">
                                                    <div className="form-group mb-4">
                                                        <label>CGST</label>
                                                        <div className="input-group mb-2">
                                                            <input
                                                                type="number"
                                                                name="cgst"
                                                                readOnly={isviewonly}
                                                                className="form-control"
                                                                value={rawProduct.cgst}
                                                                onChange={(e) => handleDynamicChange(e, index, 'cgst')}
                                                                placeholder="Enter CGST"
                                                            />
                                                            <div className="input-group-prepend">
                                                                <div className="input-group-text h-100">%</div>
                                                            </div>

                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="form-group mb-4">
                                                        <label>SGST</label>
                                                        <div className="input-group mb-2">
                                                            <input
                                                                type="number"
                                                                name="sgst"
                                                                readOnly={isviewonly}
                                                                className="form-control"
                                                                value={rawProduct.sgst}
                                                                onChange={(e) => handleDynamicChange(e, index, 'sgst')}
                                                                placeholder="Enter SGST"
                                                            />
                                                            <div className="input-group-prepend">
                                                                <div className="input-group-text h-100">%</div>
                                                            </div>

                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        ):(
                                            <div className="col-md-3">
                                                <div className="form-group mb-4">
                                                    <label>GST</label>
                                                    <div className="input-group mb-2">
                                                        <input
                                                            type="number"
                                                            name="gst"
                                                            readOnly={isviewonly}
                                                            className="form-control"
                                                            value={rawProduct.gst}
                                                            onChange={(e) => handleDynamicChange(e, index, 'gst')}
                                                            placeholder="Enter GST"
                                                        />
                                                        <div className="input-group-prepend">
                                                            <div className="input-group-text h-100">%</div>
                                                        </div>

                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Discount Type */}
                                        <div className="col-md-2">
                                            <div className="form-group mb-4">
                                                <label>HSN/SAC Code</label>
                                                <input type="text" className="form-control" readOnly={isviewonly} value={rawProduct.hsn_sac} onChange={(e) => handleDynamicChange(e, index, 'hsn_sac')} placeholder="Enter HSN/SAC Code"/>
                                            </div>
                                        </div>


                                        {/* Total Amount */}
                                        <div className="col-md-3">
                                            <div className="form-group mb-4">
                                                <label>Total Amount</label>
                                                <div className="input-group mb-2">
                                                    <div className="input-group-prepend">
                                                        <div className="input-group-text h-100">₹</div>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        name="total_amount"
                                                        readOnly={isviewonly}
                                                        className="form-control"
                                                        value={rawProduct.total_amount}
                                                        onChange={(e) => handleDynamicChange(e, index, 'total_amount')}
                                                        placeholder="Enter Total Amount"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <div className="col-md-3">
                                            <div className="form-group mb-4">
                                                <label>Description</label>
                                                <input
                                                    type="text"
                                                    name="description"
                                                    readOnly={isviewonly}
                                                    className="form-control"
                                                    value={rawProduct.description}
                                                    onChange={(e) => handleDynamicChange(e, index, 'description')}
                                                    placeholder="Enter Description"
                                                />
                                            </div>
                                        </div>

                                        {/* Add/Remove Row Buttons */}
                                        <div className="col-md-1">
                                            {index !== 0 && !isviewonly && (
                                                <button
                                                    type="button"
                                                    className="btn btn-danger mt-4"
                                                    onClick={() => {
                                                        const updatedProducts = formData.raw_products.filter((_, idx) => idx !== index);
                                                        setFormData({ ...formData, raw_products: updatedProducts });
                                                    }}
                                                >
                                                    <Minus />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <hr/>
                    <div className="col-md-12">
                        <div className="row">
                            <div className="col-md-2">
                                
                                <div className="form-group">
                                    <label>Total Quantity</label>
                                    <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text h-100">Piece</div>
                                        </div>
                                        <input type="number" className="form-control" value={formData.total_qty} name="total_qty" placeholder="Total Qty" readOnly/>

                                    </div>
                                    
                                </div>
                            </div>
                            
                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Total Taxable Value</label>
                                <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text h-100">₹</div>
                                        </div>
                                <input type="number" className="form-control" value={formData.total_taxable_value} name="total_taxable_value" placeholder="Total Taxable Value" readOnly/>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Product Discount</label>
                                <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text h-100">₹</div>
                                        </div>
                                <input type="number" className="form-control" value={formData.product_discount} name="product_discount" placeholder="Product Discount" readOnly/>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="form-group">
                                <label>Additional Discount</label>
                                <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text h-100">₹</div>
                                        </div>
                                <input type="number" className="form-control" onChange={handleChange} value={formData.additional_discount}  readOnly={isviewonly} name="additional_discount" placeholder="Additional Discount"/>
                                </div>
                            </div>
                        </div>
                        {companyData?.state == formData.state?(
                            <>
                                <div className="col-md-2">
                                    <div className="form-group">
                                        <label>CGST</label>
                                        <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text h-100">₹</div>
                                        </div>
                                        <input type="number" className="form-control" value={formData.cgst} name="cgst" placeholder="CGST %" readOnly/>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className="form-group">
                                        <label>SGST</label>
                                        <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text h-100">₹</div>
                                        </div>
                                        <input type="number" className="form-control" value={formData.sgst} name="sgst" placeholder="SGST %" readOnly/>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ):(
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label>IGST</label>
                                    <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text h-100">₹</div>
                                        </div>
                                    <input type="number" className="form-control" value={formData.igst} name="igst" placeholder="IGST %" readOnly/>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Total Amount</label>
                                <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text h-100">₹</div>
                                        </div>
                                <input type="number" className="form-control" value={formData.total_amount} name="total_amount" placeholder="Total Amount" readOnly/>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Total Return Amount </label>
                                <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text h-100">₹</div>
                                        </div>
                                <input type="number" className="form-control" value={formData.return_amount} name="return_amount" placeholder="Return Amount" readOnly/>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Advanced Vendor Payment </label>
                                <div className="input-group mb-2">
                                        <div className="input-group-prepend">
                                            <div className="input-group-text h-100">₹</div>
                                        </div>
                                <input type="number" className="form-control" onChange={handleChange} value={formData.advance_vendor_payment} name="advance_vendor_payment" placeholder="Advanced Payment"/>
                                </div>
                            </div>
                        </div>
                        
                        </div>
                    </div>

                    {!isviewonly && (<div className="d-flex justify-content-end mt-3">
                        <button
                            type="button"
                            className="btn btn-primary me-2"
                            onClick={handleAddRow}
                        >
                            <Plus /> Add More
                        </button>
                        <button
                            type="button"
                            className="btn btn-dark me-2"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-success">
                            Save
                        </button>
                    </div>)}
                </div>
            </form>
        </Modal>
    );
};

export default PurchasesProductForm;
